import { BUTTON_HEIGHT, Button, ButtonTheme } from 'components/Button';
import { ICONS } from 'components/Icon';
import { useConfirm } from 'hooks/useConfirm';
import { theme } from 'lib/theme';
import React from 'react';
import { Platform, StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { useDispatch } from 'react-redux';
import { AnyAction } from 'redux';
import { FileSourceType } from 'service/file/types';
import { DO_IMAGE_SELECT } from 'store/actions';

interface Props {
  style?: StyleProp<ViewStyle>;
  enableRemoveButton?: boolean;
  action: AnyAction;
  removeConfirmMessage?: string;
  shadow?: boolean;
}

export const ImageButtons: React.FC<Props> = ({
  action,
  enableRemoveButton = false,
  removeConfirmMessage = 'Do you want to remove this image?',
  shadow,
  style,
}) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();

  const handleQuickAdd =
    (source: FileSourceType.CAMERA | FileSourceType.IMAGE) => () =>
      dispatch(
        DO_IMAGE_SELECT({
          action,
          source,
        }),
      );

  const handleRemoveFiles = () => {
    confirm({
      confirmText: 'Remove',
      destructive: true,
      onConfirm: () =>
        dispatch({ ...action, payload: { ...action.payload, image: null } }),
      text: removeConfirmMessage,
    });
  };

  const buttonChildren = [];

  if (Platform.OS !== 'web') {
    buttonChildren.push(
      <Button
        buttonTheme={ButtonTheme.themeless}
        icon={ICONS.CameraLight}
        onPress={handleQuickAdd(FileSourceType.CAMERA)}
        style={styles.button}
      />,
      <Button
        buttonTheme={ButtonTheme.themeless}
        icon={ICONS.ImageLight}
        onPress={handleQuickAdd(FileSourceType.IMAGE)}
        style={styles.button}
      />,
    );
  } else {
    buttonChildren.push(
      <Button
        buttonTheme={ButtonTheme.themeless}
        icon={ICONS.ImageLight}
        onPress={handleQuickAdd(FileSourceType.IMAGE)}
        style={styles.button}
      />,
    );
  }

  if (enableRemoveButton) {
    buttonChildren.push(
      <Button
        buttonTheme={ButtonTheme.themeless}
        onPress={handleRemoveFiles}
        style={styles.button}
        icon={ICONS.TrashCanLight}
      />,
    );
  }

  return (
    <View
      style={[styles.buttons, shadow ? styles.buttonsShadow : undefined, style]}
    >
      {React.Children.toArray(buttonChildren).map((child, index) => (
        <React.Fragment key={index}>
          {index > 0 && <View style={styles.divider} />}
          {child}
        </React.Fragment>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  button: {
    borderRadius: 0,
    height: BUTTON_HEIGHT - 2,
    paddingLeft: theme.paddingHorizontal - 2,
    paddingRight: theme.paddingHorizontal - 2,
  },
  buttons: {
    backgroundColor: 'white',
    borderColor: theme.colors.blue80,
    borderRadius: 4,
    borderWidth: 1,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  buttonsShadow: {
    shadowColor: theme.colors.blue,
    shadowOffset: { height: 0, width: 0 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
  },
  divider: {
    borderRightColor: theme.colors.blue80,
    borderRightWidth: 1,
  },
});
